import { Elements, useStripe } from "@stripe/react-stripe-js";
import StripeForm from "./StripeForm";
import { loadStripe } from "@stripe/stripe-js";
import { useContext, useEffect, useMemo, useState } from "react";
import { createPaymentIntent, IPaymentDetailOptions, successPaymentStatus } from "./paymentIntent";
import { Context, IContext } from "../../context/ContextApp";
import { getStripeSecretKey } from "../../utils/environmentVariablesHelper";
import { useParams } from "react-router-dom";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";

interface IPaymentInfo {
  paymentDetailsOptions: IPaymentDetailOptions | null;
  clientSecret: string;
  status: string;
}

const PaymentStatusScreen = ({
  title,
  orderId,
  children,
}: {
  title: string;
  orderId: string;
  children: React.ReactNode;
}) => (
  <div style={{ textAlign: "center", width: "100%", padding: "10em" }}>
    <h1>{title}</h1>
    {children}
    <a href={`/order-history?order=${orderId}`}>Go back to order history</a>
  </div>
);

const RequiresActionHandler = ({
  clientSecret,
  orderId,
}: {
  clientSecret: string;
  orderId: string;
}) => {
  const stripe = useStripe();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!stripe) return;
    stripe.handleNextAction({ clientSecret }).then(({ error, paymentIntent }) => {
      if (error) {
        setError(error.message ?? "Authentication failed. Please try again.");
      } else if (paymentIntent?.status === "succeeded" || paymentIntent?.status === "processing") {
        window.location.reload();
      } else {
        setError("Authentication was not completed. Please try again.");
      }
    });
  }, [stripe, clientSecret]);

  if (error) {
    return (
      <div style={{ textAlign: "center", padding: "2em" }}>
        <h2>Authentication failed</h2>
        <p>{error}</p>
        <a href={`/order-history?orderId=${orderId}`}>← Back to order history</a>
      </div>
    );
  }

  return (
    <div style={{ textAlign: "center", width: "100%", padding: "10em" }}>
      <h1>Authentication required</h1>
      <p>Please complete the authentication in the popup to confirm your payment.</p>
    </div>
  );
};

const PaymentForm = () => {
  const { appBranch }: IContext = useContext(Context);
  const stripeSecretKey = getStripeSecretKey(appBranch);
  const [paymentInfo, setPaymentInfo] = useState<IPaymentInfo>();
  const [error, setError] = useState<string | null>(null);
  const stripePromise = useMemo(() => loadStripe(stripeSecretKey), [stripeSecretKey]);
  const { orderId } = useParams<{ orderId: string }>();

  useEffect(() => {
    if (!paymentInfo && !error && orderId) {
      const loadPaymentIntent = async () => {
        const response = await createPaymentIntent(orderId);
        if (response.status === 200) {
          setPaymentInfo({
            paymentDetailsOptions: response.payment_details_options,
            clientSecret: response.clientSecret,
            status: response.message,
          });
        } else if (response.status === 401) {
          setError("We could not find your order.");
        } else {
          setError("We have problems with Stripe API, please try again later.");
        }
      };
      loadPaymentIntent();
    }
  }, [paymentInfo, error, orderId]);

  if (!orderId) {
    return (
      <div style={{ textAlign: "center", padding: "2em" }}>
        <h2>Oops! Something went wrong</h2>
        <p>We could not find your order. Please try again in a few minutes.</p>
        <a href="/order-history" style={{ display: "inline-block", marginTop: "1em" }}>
          ← Back to order history
        </a>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: "center", padding: "2em" }}>
        <h2>Oops! Something went wrong</h2>
        <p>{error}</p>
        <a
          href={`/order-history?orderId=${orderId}`}
          style={{ display: "inline-block", marginTop: "1em" }}
        >
          ← Back to order history
        </a>
      </div>
    );
  }

  if (!paymentInfo) {
    return (
      <div className="spinner-wrapper">
        <LoadingSpinner />
      </div>
    );
  }

  const { status, clientSecret, paymentDetailsOptions } = paymentInfo;

  if (status === successPaymentStatus.payment_confirmed) {
    return (
      <PaymentStatusScreen title="Payment confirmed ✅" orderId={orderId}>
        <p>Thank you for your purchase!</p>
        <p>You will receive an email confirmation shortly.</p>
      </PaymentStatusScreen>
    );
  }

  if (status === successPaymentStatus.payment_processing) {
    return (
      <PaymentStatusScreen title="Payment processing ⏳" orderId={orderId}>
        <p>Your payment is being processed.</p>
        <p>You will receive an email confirmation once it is complete.</p>
      </PaymentStatusScreen>
    );
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      {status === successPaymentStatus.payment_requires_action ? (
        <RequiresActionHandler clientSecret={clientSecret} orderId={orderId} />
      ) : (
        <StripeForm orderId={orderId} paymentDetailsOptions={paymentDetailsOptions} />
      )}
    </Elements>
  );
};

export default PaymentForm;
